import { useMutation } from '@tanstack/react-query';
import { getFunctions } from 'firebase/functions';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';

/**
 * Interface for submitting AI feedback
 */
interface SubmitFeedbackInput {
  /** Unique identifier for the AI output being rated */
  outputId: string;
  /** Type of feedback: helpful, not_helpful, or flagged */
  feedbackType: 'helpful' | 'not_helpful' | 'flagged';
  /** Optional text comment for additional context */
  comment?: string;
}

/**
 * Hook to submit feedback on AI-generated outputs
 *
 * Enables users to rate AI results with thumbs up/down or flag issues.
 * This feedback drives the recursive learning loop for AI improvement.
 *
 * @example
 * ```tsx
 * const { mutate: submitFeedback, isPending } = useAIFeedback();
 *
 * submitFeedback(
 *   { outputId: 'output-123', feedbackType: 'helpful' },
 *   { onSuccess: () => toast.success('Thanks!') }
 * );
 * ```
 *
 * @returns React Query mutation for submitting feedback
 */
export function useAIFeedback() {
  return useMutation({
    mutationFn: async (input: SubmitFeedbackInput) => {
      const functions = getFunctions();
      const submitFeedbackFn = httpsCallable(functions, 'submitAIFeedback');

      // Transform parameter names to match Cloud Function contract
      const result = await submitFeedbackFn({
        aiOutputId: input.outputId,
        feedbackType: input.feedbackType,
        reason: input.comment,
      });

      return result.data;
    },
  });
}
